// Custom handler for Firebase auth action links (password reset, email verification).
// Pointed at by the "Action URL" customized in Firebase Console -> Authentication -> Templates.
// Replaces Firebase's hosted reset page so we can render a strong-password-friendly form.
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode
} from 'firebase/auth';
import { auth } from '../firebase/config';
import '../styles/Login.css';

const AuthAction = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  const [phase, setPhase] = useState('verifying');
  const [accountEmail, setAccountEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const passwordFieldRef = useRef(null);
  const confirmFieldRef = useRef(null);

  useEffect(() => {
    if (!oobCode) {
      setError('Invalid or missing reset code. Please request a new password reset link.');
      setPhase('error');
      return;
    }

    if (mode === 'resetPassword') {
      verifyPasswordResetCode(auth, oobCode)
        .then((email) => {
          setAccountEmail(email);
          setPhase('reset');
        })
        .catch((err) => {
          console.error('verifyPasswordResetCode error:', err);
          if (err.code === 'auth/expired-action-code') {
            setError('This password reset link has expired. Please request a new one from the login page.');
          } else if (err.code === 'auth/invalid-action-code') {
            setError('This password reset link is invalid or has already been used. Please request a new one.');
          } else {
            setError('Could not verify the reset link. Please request a new one.');
          }
          setPhase('error');
        });
    } else if (mode === 'verifyEmail') {
      applyActionCode(auth, oobCode)
        .then(() => setPhase('verified'))
        .catch((err) => {
          console.error('applyActionCode error:', err);
          setError('Could not verify your email. The link may be expired or already used.');
          setPhase('error');
        });
    } else {
      setError('Unsupported action.');
      setPhase('error');
    }
  }, [mode, oobCode]);

  const passwordRules = [
    { label: '8+ characters', test: (p) => p.length >= 8 },
    { label: 'Uppercase letter', test: (p) => /[A-Z]/.test(p) },
    { label: 'Special character', test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(p) }
  ];

  const allRulesMet = passwordRules.every((r) => r.test(password));
  const passwordsMatch = password && password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!allRulesMet) {
      setError('Password does not meet the requirements below.');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setPhase('success');
    } catch (err) {
      console.error('confirmPasswordReset error:', err);
      if (err.code === 'auth/expired-action-code') {
        setError('This reset link has expired. Please request a new one.');
      } else if (err.code === 'auth/invalid-action-code') {
        setError('This reset link is invalid or has already been used.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Choose a stronger password.');
      } else {
        setError('Could not reset password. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleFocus = (ref) => ref.current?.classList.add('focused');
  const handleBlur = (ref) => ref.current?.classList.remove('focused');

  const EyeIcon = ({ show }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      {show ? (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </>
      ) : (
        <>
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </>
      )}
    </svg>
  );

  return (
    <div className="login-wrapper">
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo">
            <img src="/bt_logo.jpg" alt="Brooklyn Tabernacle Logo" />
          </div>
          <h1>
            {phase === 'reset' && 'Reset your password'}
            {phase === 'success' && 'Password reset!'}
            {phase === 'verified' && 'Email verified'}
            {phase === 'verifying' && 'Verifying link…'}
            {phase === 'error' && 'Link not valid'}
          </h1>
          {phase === 'reset' && accountEmail && (
            <p className="login-subtitle">for <strong>{accountEmail}</strong></p>
          )}
        </div>

        {phase === 'verifying' && (
          <div className="auth-action-loading">
            <div className="spinner-inline" style={{ borderTopColor: '#ccad39' }}></div>
          </div>
        )}

        {phase === 'reset' && (
          <form
            onSubmit={handleSubmit}
            className="email-auth-form"
          >
            {/* Hidden username field so password managers can pair the suggested
                password with the right account. Read-only so it doesn't get
                clobbered by autofill. */}
            <input
              type="email"
              name="username"
              autoComplete="username"
              value={accountEmail}
              readOnly
              style={{ display: 'none' }}
            />

            <div className="form-field password-field" ref={passwordFieldRef}>
              <label htmlFor="new-password">New password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="new-password"
                name="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => handleFocus(passwordFieldRef)}
                onBlur={() => handleBlur(passwordFieldRef)}
                required
                minLength={8}
                autoComplete="new-password"
                placeholder="Enter a new password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                <EyeIcon show={showPassword} />
              </button>
            </div>

            <div className="form-field password-field" ref={confirmFieldRef}>
              <label htmlFor="confirm-password">Confirm new password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="confirm-password"
                name="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={() => handleFocus(confirmFieldRef)}
                onBlur={() => handleBlur(confirmFieldRef)}
                required
                minLength={8}
                autoComplete="new-password"
                placeholder="Re-enter the new password"
              />
            </div>

            <ul className="password-rules">
              {passwordRules.map((rule) => (
                <li key={rule.label} className={rule.test(password) ? 'met' : ''}>
                  {rule.test(password) ? '✓' : '○'} {rule.label}
                </li>
              ))}
              <li className={passwordsMatch ? 'met' : ''}>
                {passwordsMatch ? '✓' : '○'} Passwords match
              </li>
            </ul>

            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || !allRulesMet || !passwordsMatch}
            >
              {submitting ? <div className="spinner-inline"></div> : 'Save new password'}
            </button>

            {error && (
              <div className="message error-message">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{error}</span>
              </div>
            )}
          </form>
        )}

        {phase === 'success' && (
          <>
            <div className="message success-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span>Your password has been updated. You can now sign in with your new password.</span>
            </div>
            <button
              type="button"
              className="submit-btn"
              onClick={() => navigate(`/login?email=${encodeURIComponent(accountEmail)}`)}
              style={{ marginTop: '1.5rem' }}
            >
              Go to sign in
            </button>
          </>
        )}

        {phase === 'verified' && (
          <>
            <div className="message success-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span>Your email address has been verified.</span>
            </div>
            <button
              type="button"
              className="submit-btn"
              onClick={() => navigate('/login?verified=true')}
              style={{ marginTop: '1.5rem' }}
            >
              Continue to sign in
            </button>
          </>
        )}

        {phase === 'error' && (
          <>
            <div className="message error-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{error}</span>
            </div>
            <button
              type="button"
              className="submit-btn"
              onClick={() => navigate('/login')}
              style={{ marginTop: '1.5rem' }}
            >
              Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthAction;
